import React, { memo, useMemo, useCallback, DragEvent } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { ImageBlockSettings } from './ImageBlockSettings';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Container } from 'reactstrap';
import { FormHTMLInline } from '@core/components/form';
import { DropableImage } from '@modules/common/DropableImage';
import { ProposalService } from '@business/services';
import { useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { R } from '@core/utils/r';
import { openAttachLinkModal } from './AttachLinkModal';

export enum ImageBlockVariation {
  imageCentered = 'imageCentered',
  imageFullWidth = 'imageFullWidth',
  imageWithText = 'imageWithText',
  textOnImage = 'textOnImage',
}

function initialImageForVariation(variation: ImageBlockVariation) {
  switch (variation) {
    case ImageBlockVariation.imageCentered:
      return 'https://images.unsplash.com/photo-1541584285245-c83a93cce0e8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHwxMnx8YmljeWNsZXxlbnwwfHx8fDE2NzkzMzY2NDQ&ixlib=rb-4.0.3&q=80&w=500';
    case ImageBlockVariation.imageFullWidth:
      return 'https://images.riseusercontent.com/f:jpg|png,a:retain,b:fff/assets/rise/assets/block-defaults/ocean.jpg';
    case ImageBlockVariation.imageWithText:
      return 'https://images.unsplash.com/photo-1547924326-032f60c4ada3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHwxMnx8YnVzJTIwc3F1YXJlfGVufDB8fHx8MTY3OTMzNjkxNg&ixlib=rb-4.0.3&q=80&w=400';
    case ImageBlockVariation.textOnImage:
      return 'https://images.unsplash.com/photo-1536599018102-9f803c140fc1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHw4fHxidWlsZGluZ3MlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTY3OTMzNjgwOA&ixlib=rb-4.0.3&q=80&w=1200';
  }
}

function defineImageBlockForm(variation: ImageBlockVariation) {
  return formGroup({
    image: formControl<string>({
      initialValue: initialImageForVariation(variation),
    }),
    text: formControl<string>({
      initialValue:
        'Vivamus conubia tellus fames torquent id congue gravida lorem, orci habitant massa mus erat turpis rhoncus, laoreet suscipit convallis felis mauris lectus nisi.',
    }),
    size: formControl<'contain' | 'cover' | 'fill'>({ label: 'Image Size', initialValue: 'cover' }),
    imagePosition: formControl<'image-right' | 'image-left'>({ label: 'Image Position', initialValue: 'image-left' }),
    imageWidth: formControl<string>({ label: 'Image Width', initialValue: 'image-w-50' }),
    backdropColor: formControl<string>({ label: 'Background Overlay Color', initialValue: '#000000' }),
    backdropOpacity: formControl<string>({ label: 'Background Overlay Opacity (%)', initialValue: '30' }),
    // Optional hyperlink on the image. 'designFile' resolves per proposal to the Design File chosen
    // in the proposal wizard, so a template can link to a file that doesn't exist yet.
    linkType: formControl<'none' | 'designFile' | 'url'>({ label: 'Link', initialValue: 'none' }),
    linkUrl: formControl<string>({ label: 'Web Address', placeholder: 'https://example.com' }),
    linkNewTab: formControl<boolean>({ label: 'Open in a new tab', initialValue: true }),
    advance: defineBlockAdvanceForm(),
  });
}

export type ImageWidgetFormData = FormDataType<typeof defineImageBlockForm>;
export type ImageWidgetForm = ReturnType<typeof defineImageBlockForm>;

export const ImageBlock = memo((props: Proposal.BlockProps<ImageBlockVariation>) => {
  const { variation, preview, editable } = props;
  const form = useMemo(() => defineImageBlockForm(variation), [variation]);
  const formValue = useNonNilObservable(form.value$);
  const data = useProposalDataContext();

  const imgSrc = useMemo(() => {
    const defaultImage = initialImageForVariation(variation);
    const allDefaultImages = [
      'https://images.unsplash.com/photo-1541584285245-c83a93cce0e8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHwxMnx8YmljeWNsZXxlbnwwfHx8fDE2NzkzMzY2NDQ&ixlib=rb-4.0.3&q=80&w=500',
      'https://images.riseusercontent.com/f:jpg|png,a:retain,b:fff/assets/rise/assets/block-defaults/ocean.jpg',
      'https://images.unsplash.com/photo-1547924326-032f60c4ada3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHwxMnx8YnVzJTIwc3F1YXJlfGVufDB8fHx8MTY3OTMzNjkxNg&ixlib=rb-4.0.3&q=80&w=400',
      'https://images.unsplash.com/photo-1536599018102-9f803c140fc1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHw4fHxidWlsZGluZ3MlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTY3OTMzNjgwOA&ixlib=rb-4.0.3&q=80&w=1200',
    ];

    if (allDefaultImages.indexOf(formValue.image) >= 0) {
      return defaultImage;
    }
    return formValue.image;
  }, [formValue.image, variation]);

  useConfigureBlock({
    props,
    form,
    contentComponent: ImageBlockSettings,
    contentProps: { form, variation },
  });

  const onDropImage = useCallback(
    (file: File) => {
      UploadAPI.upload('proposal/images', file)
        .pipe(tap(res => form.patchValue({ image: res.fileUrl })))
        .subscribe();
    },
    [form],
  );

  const handleDrag = useCallback(
    (e: DragEvent<HTMLImageElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0];
        const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];

        if (validImageTypes.includes(file.type)) {
          onDropImage(file);
        }
      }
    },
    [onDropImage],
  );

  const textContent = useMemo(() => {
    const content = (
      <div className="text">
        <FormHTMLInline control={form.controls.text} readonly={!editable} transformedFieldsValue={ProposalService.getTransformedValues(data)} />
      </div>
    );
    if (variation === ImageBlockVariation.textOnImage) {
      return <Container style={{ display: 'flex' }}>{content}</Container>;
    } else if (variation === ImageBlockVariation.imageWithText) {
      return content;
    }
    return null;
  }, [form.controls.text, editable, data, variation]);

  // Where the image links to on the finished proposal. The Design File option stays empty while the
  // template is being built (nothing selected yet), so the image simply isn't clickable there.
  const linkHref = useMemo(() => {
    if (formValue.linkType === 'designFile') return data?.designFileUrl || '';
    if (formValue.linkType === 'url') return formValue.linkUrl || '';
    return '';
  }, [formValue.linkType, formValue.linkUrl, data?.designFileUrl]);

  // The link is a click handler on the <img> itself, NOT an <a> wrapper: every sizing rule in
  // index.scss targets `> img` (direct child), so a wrapper dropped them and the image rendered at
  // its natural (huge) size. Active only in read-only views — in the builder the image stays
  // draggable/replaceable.
  const isLinked = !editable && !!linkHref;
  const image = (
    <DropableImage
      style={{ objectFit: formValue.size, cursor: isLinked ? 'pointer' : undefined }}
      src={imgSrc}
      onDropImage={onDropImage}
      onClick={isLinked ? () => window.open(linkHref, formValue.linkNewTab ? '_blank' : '_self', 'noopener,noreferrer') : undefined}
      title={isLinked ? 'Open link' : undefined}
    />
  );

  // Visible affordance in the builder: a badge on the image opens the link dialog. (The same
  // settings also live in the block's settings drawer.)
  const linkBadge = editable && (
    <a
      onClick={event => {
        // The block toolbar sits above this; stop the click reaching the block behind it.
        event.preventDefault();
        event.stopPropagation();
        openAttachLinkModal({ form });
      }}
      title="Attach a link to this image"
      style={{
        position: 'absolute',
        // Clear of `.block-toolbar` (absolute, top:0, full width, z-index:10, shown on hover) —
        // at the top it covered this badge, so clicks landed on the toolbar instead.
        bottom: 8,
        right: 8,
        zIndex: 11,
        background: 'rgba(0, 0, 0, 0.6)',
        color: '#fff',
        borderRadius: 4,
        padding: '4px 8px',
        fontSize: 12,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <i className="bx bx-link mr-1" />
      {formValue.linkType === 'designFile' ? 'Links to Design File' : formValue.linkType === 'url' ? 'Linked' : 'Add Link'}
    </a>
  );

  const content = (
    <BlockContainer
      className={classNames('d-flex flex-fill', 'block-image', variation, preview, formValue.imagePosition, formValue.imageWidth)}
      advanceForm={formValue.advance}
      editable={editable}
      isSection={props.isSection}
      sectionTitle={props.sectionTitle}
      // Anchor for the absolutely-positioned link badge
      style={{ position: 'relative' }}
    >
      {linkBadge}
      {textContent}
      {variation === ImageBlockVariation.textOnImage && (
        <div
          onDrag={handleDrag}
          onDrop={handleDrag}
          onDragOver={handleDrag}
          className="backdrop"
          style={{ backgroundColor: formValue.backdropColor, opacity: R.toNumber(formValue.backdropOpacity) / 100 }}
        ></div>
      )}
      {image}
    </BlockContainer>
  );

  return [ImageBlockVariation.imageFullWidth, ImageBlockVariation.textOnImage].includes(variation) ? (
    content
  ) : (
    <div style={{ backgroundColor: formValue.advance.backgroundColor }}>
      <Container>{content}</Container>
    </div>
  );
});
